import { useMemo } from 'react';

import { IClientAllocationGroup } from '~/services/api/clientAllocation/types';
import { IDateRange, IParams } from '~/services/api/types';
import { IClientAllocationFilters, IClientAllocationStore } from '~/store/clientAllocation/types';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { getLocalDateNoFormat } from '~/utils/date/date';
import AllocationTypes from '../../reusable/ClientAllocationForm/constants/allocationTypes';

interface IUseGetAllocationsProps {
  clinicId: string;
  actTeamId: string;
  isCrisis: boolean;
}

interface IUseGetAllocationsReturn {
  getAllocations: (payload: {
    clinicId: string;
    teamId: string;
    params?: IParams & IDateRange;
  }) => Promise<void>;
  list: IClientAllocationGroup[];
  dateRange: Date[];
  currentFilters: {
    filters: IClientAllocationFilters;
    pagination: IClientAllocationStore['pagination'];
  };
}

const useGetAllocations = ({
  isCrisis = false,
}: IUseGetAllocationsProps): IUseGetAllocationsReturn => {
  const { isGlobalUser } = useRole();

  const {
    onGetGlobalUserClientAllocations,
    onGetClientAllocationsStandard,
    onGetClientAllocationsCrisis,
  } = useStoreActions(actions => actions.clientAllocation);

  const {
    filters,
    pagination,
    standardFilters,
    crisisFilters,
    list,
    standardList,
    crisisList,
    crisisPagination,
    standardPagination,
  } = useStoreState(state => state.clientAllocation);

  const currentFilters = useMemo(() => {
    if (isGlobalUser) {
      return {
        filters,
        pagination,
      };
    }
    return {
      filters: isCrisis ? crisisFilters : standardFilters,
      pagination: isCrisis ? crisisPagination : standardPagination,
    };
  }, [
    isGlobalUser,
    isCrisis,
    crisisFilters,
    standardFilters,
    crisisPagination,
    standardPagination,
    filters,
    pagination,
  ]);

  const getAllocations = useMemo(() => {
    if (isGlobalUser) {
      return (props: { clinicId: string; teamId: string; params?: IParams & IDateRange }) =>
        onGetGlobalUserClientAllocations({
          ...props,
          params: {
            ...props.params,
            allocationType: isCrisis ? AllocationTypes.CRISIS : AllocationTypes.STANDARD,
          },
        });
    }
    return isCrisis ? onGetClientAllocationsCrisis : onGetClientAllocationsStandard;
  }, [
    isGlobalUser,
    isCrisis,
    onGetClientAllocationsCrisis,
    onGetClientAllocationsStandard,
    onGetGlobalUserClientAllocations,
  ]);

  const currentList = useMemo(() => {
    if (isGlobalUser) {
      return list;
    }
    return isCrisis ? crisisList : standardList;
  }, [isGlobalUser, isCrisis, crisisList, standardList, list]);

  const dateRange = useMemo(
    () =>
      currentFilters.filters?.dateRange[0] && currentFilters.filters?.dateRange[1]
        ? [
            getLocalDateNoFormat(currentFilters.filters.dateRange[0]),
            getLocalDateNoFormat(currentFilters.filters.dateRange[1]),
          ]
        : [],
    [currentFilters.filters?.dateRange],
  );

  return {
    getAllocations,
    list: currentList,
    currentFilters,
    dateRange,
  };
};

export default useGetAllocations;
